import {
  AfterViewInit,
  Component,
  computed,
  input,
  signal,
  ViewChild,
  forwardRef,
  output,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatChipOption } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface SelectorElement {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-selector-chip',
  templateUrl: './selector-chip.component.html',
  styleUrls: ['./selector-chip.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatMenuModule,
    MatChipOption,
    MatButtonModule,
    MatTooltipModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectorChipComponent),
      multi: true,
    },
  ],
})
export class SelectorChipComponent
  implements AfterViewInit, ControlValueAccessor
{
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  data = input<SelectorElement[]>();
  label = input<string>('Seleccionar elemento');
  unselectOption = input<boolean>(true);
  selectedLabelPrefix = input<string>('');
  selectedIndex = input<number | null>(null);
  disabledInput = input<boolean>(false);
  // Mapa opcional de tooltips por valor del elemento
  tooltips = input<Record<string, string> | null>(null);
  elements = computed(() => this.data() || []);
  changeDisableState = output<boolean>();

  chipLabel = signal<string>('');

  selectedElement = signal<SelectorElement | null>(null);
  isMenuOpen = signal(false);

  // ControlValueAccessor implementation
  private onChange = (value: SelectorElement | null) => {};
  private onTouched = () => {};
  private _disabled = false;

  isDisabled = computed(() => this.disabledInput() || this._disabled);

  constructor() {
    // Sincroniza la etiqueta del chip con la selección y el listado de opciones
    // Debe ejecutarse en un contexto de inyección (constructor) para evitar NG0203
    effect(() => {
      const items = this.elements();
      const selected = this.selectedElement();
      if (!selected) {
        this.chipLabel.set(this.label());
        return;
      }
      const exists = items.some(i => i.value === selected.value);
      this.chipLabel.set(exists ? selected.viewValue : this.label());
    });
  }

  ngAfterViewInit(): void {
    if (this.selectedIndex() !== null && this.elements().length > 0) {
      const selected = this.elements()[this.selectedIndex()!];
      console.log('Selected element:', selected);
      this.onElementSelected(selected);
    }
  }

  onElementSelected(element: SelectorElement) {
    this.selectedElement.set(element);
    this.chipLabel.set(element.viewValue);
    this.menuTrigger.closeMenu();

    // Notificar el cambio a través de ControlValueAccessor
    this.onChange(element);
    this.onTouched();
  }

  openMenu(e) {
    e.preventDefault();
    this.isMenuOpen.set(true);
    this.menuTrigger.openMenu();
  }

  onMenuClosed() {
    this.isMenuOpen.set(false);
  }

  isSelected(elementValue: string): boolean {
    return this.selectedElement()?.value === elementValue;
  }

  // Métodos públicos para interactuar con el componente desde el exterior
  getSelectedElement(): SelectorElement | null {
    return this.selectedElement();
  }

  clearSelection(): void {
    this.selectedElement.set(null);
    this.chipLabel.set('Seleccionar Índice');

    // Notificar el cambio a través de ControlValueAccessor
    this.onChange(null);
    this.onTouched();
  }

  // ControlValueAccessor methods
  writeValue(value: SelectorElement | null): void {
    this.selectedElement.set(value);
    if (!value) {
      this.chipLabel.set(this.label());
      return;
    }
    const exists = this.elements().some(i => i.value === value.value);
    this.chipLabel.set(exists ? value.viewValue : this.label());
  }

  registerOnChange(fn: (value: SelectorElement | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  getTooltipFor(element: SelectorElement): string | null {
    const map = this.tooltips();
    if (!map) return null;
    // Búsqueda directa por value
    const direct = map[element.value];
    if (direct) return direct;
    // Corrección específica para posible inconsistencia ReCI/ReCl
    if (element.value === 'ReCl' && map['ReCI']) return map['ReCI'];
    if (element.value === 'ReCI' && map['ReCl']) return map['ReCl'];
    return null;
  }
}
